import type { CSSProperties, ReactNode } from 'react';

import type { IconTypes } from './icon/Icon.type';

// TODO: Use something from tiptap?
export type BasicNode = {
  type: string;
};

// TODO: use something from tiptap/core
export type BasicText = {
  text: string;
};

export type HardBreak = {
  type: 'hardBreak';
};

// TODO: add link element
export type InlineNode = BasicText | HardBreak;

// TODO: use something from tiptap/core
export type Path = Array<number>;

// TODO: use something from tiptap/core
export type NodeEntry<T extends BasicNode | BasicText> = [T, Path];

export type TIconEmoji = {
  type: 'emoji';
  content: string;
  position: 'left' | 'right';
};

export type TIcon =
  | {
      type: 'icon';
      iconType: 'phosphor';
      iconName: IconTypes;
    }
  | TIconEmoji;

export type EmptyChildren = [{ id: string; text: '' }];

export type DreamRenderElementProps<T extends BasicNode> = {
  translations?: Record<string, string>;
  children: React.ReactNode;
  element: T;
  attributes?: {
    [key: `data-${string}`]: any;
  };
  as?: React.ElementType;
  className?: string;
  [key: string]: any;
  isLive?: boolean;
};

export type DreamRenderLeafProps<T extends BasicText> = {
  leaf: T;
  attributes?: {
    [key: `data-${string}`]: any;
  };
  className?: string;
  children?: any;
};

export type AlignType = 'left' | 'center' | 'right' | 'justify' | 'column_dynamic';

export type JustifyType = 'start' | 'center' | 'end';

export type VerticalAlignType =
  | 'align_start'
  | 'align_center'
  | 'align_end'
  | 'space_between'
  | 'space_around'
  | 'space_evenly';

export type CustomAttribute = {
  id?: string;
  className?: string;
  [key: string]: unknown;
};

export type BlockAnimationType = 'fade_in' | 'slide_in' | 'scale_in';

// Animation
export type BlockAnimationFadeIn = {
  type: 'fade_in';
};

export type BlockAnimationSlideIn = {
  type: 'slide_in';
  direction: 'left' | 'right' | 'up' | 'down';
};

export type BlockAnimationScaleIn = {
  type: 'scale_in';
  scaleTo: 'grow' | 'shrink';
};

export type BlockAnimation = BlockAnimationFadeIn | BlockAnimationSlideIn | BlockAnimationScaleIn;

export type LeafAnimationType = 'scroll_words';

export type LeafAnimationScrollWords = {
  type: 'scroll_words';
  words: string[];
};
export type LeafAnimation = LeafAnimationScrollWords;

// *****************************************
// START OF FONT TYPES
// *****************************************
export interface GoogleFontData {
  family: string;
  variants: string[];
  subsets: string[];
  version: string;
  lastModified: string;
  files: Record<string, string>;
  category: string;
  kind: string;
  menu: string;
}

export interface GoogleFontMap {
  [key: string]: {
    family: string;
    variants: string[];
    subsets: string[];
    version: string;
    lastModified: string;
    files: Record<string, string>;
    category: string;
    kind: string;
    menu: string;
  };
}

export interface GoogleFontResponse {
  kind: string;
  items: GoogleFontData[];
}

export interface FontStyleMap {
  [key: string]: { title: string; label?: string; styles: React.CSSProperties };
}

export type FontSelectionMap = Record<string, string>;

export type FontWeightSelectionMap = Record<string, number>;
// *****************************************
// END OF FONT TYPES
// *****************************************

export type NavbarItemType = 'link' | 'button' | 'dropdown' | 'search' | 'logo' | 'user_button';
export type NavbarDropdownStyleType = 'full' | 'card';
export type NavbarDropdownItemStyleType = 'link' | 'description' | 'icon' | 'featured';
export type NavbarUserButtonStyleType = 'button' | 'dropdown';

// ****************************************************
// START OF PRICING and PAID-TIER ELEMENT SHARED TYPES
// ****************************************************
export type PremiumOffer = {
  id: string;
  card_required: boolean;
  description: string;
  expires_at: string | null;
  name: string;
  pct_discount: number | null;
  terms: string;
  trial_period_days: number | null;
  discounted_benefit?: string;
};

export interface TierData {
  name: string;
  id: string;
}

export enum PriceInterval {
  MONTH = 'month',
  YEAR = 'year',
  ONE_TIME = 'one_time',
  DONATION = 'donation',
}

export interface Price {
  id: string;
  amount_cents: number;
  original_amount_cents: number;
  formatted_amount?: string;
  currency: string;
  interval: PriceInterval;
  cta: string;
  denominator?: string;
  features: string[];
  premium_offer?: PremiumOffer;
}

export enum TierStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export interface Tier {
  id: string;
  publication_id: string;
  name: string;
  prices: Price[];
  status: TierStatus;
  description?: string;
  redirect_url?: string;
  gifting_enabled?: boolean;
  enabled: boolean;
  should_split_prices?: boolean;
}

export type StripeSessionIds = Record<string, string>;

export type StripeCardElementComponent = React.FunctionComponent<{ className?: string; options?: any }>;
export type CardlessPremiumOfferSignupElementComponent = React.FunctionComponent;
export type PaymentRequestButtonElementComponent = ReactNode;

// ****************************************************
// START OF RECOMMENDATION ELEMENT SHARED TYPES
// ****************************************************

export interface Recommendation {
  id: string;
  name: string;
  description: string | null;
  logo_url: string;
  site_url: string;
  sponsored: boolean;
  recommended_publication_id: string;
  isOwner: boolean;
}

// ****************************************************
// END OF RECOMMENDATION ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST ELEMENT SHARED TYPES
// ****************************************************

interface ProfilePicture {
  landscape: URL;
  thumb: URL;
  url: string;
}

export interface Author {
  id: string;
  name: string;
  bio: string;
  profile_picture: ProfilePicture;
  twitter_handle?: string;
  facebook_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  discord_url?: string;
}

export type ContentTag = {
  id: string;
  display: string;
};

export interface PostData {
  id: string;
  web_title: string;
  web_subtitle?: string;
  comments_state: string;
  override_scheduled_at: string;
  scheduled_at: string;
  /** @deprecated use is_premium instead (Never Remove, some older tip-tap content still uses this */
  audience: 'premium' | 'free' | 'both';
  is_premium: boolean;
  slug: string;
  content_tags: ContentTag[];
  image_url: string;
  authors: Author[];
  estimated_reading_time: number | null;
  estimated_reading_time_display: string | null;
}

export type PostCardSchema = {
  node: string;
  className: string;
  children: PostCardSchema[];
};

// ****************************************************
// END OF POST ELEMENT SHARED TYPES
// ****************************************************

interface URL {
  url: string;
}
export interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// ****************************************************
// START OF CAROUSEL/SLIDER ELEMENT SHARED TYPES
// ****************************************************

export type SliderControlsDisplayType = 'inline' | 'overlay';

// ****************************************************
// END OF CAROUSEL/SLIDER SHARED TYPES
// ****************************************************

// ****************************************************
// START OF PAYWALL ELEMENT SHARED TYPES
// ****************************************************

export interface IPaywallStyle {
  show_image: boolean;
  background_color: string;
  text_color: string;
  button_background_color: string;
  button_text_color: string;
  container_shadow: string;
}

export interface Paywall {
  id: string;
  name: string;
  title: string;
  description: string;
  cta_text: string;
  subheader: string;
  benefits: string[];
  website_default: boolean;
  offer_id?: string;
  theme: PostTheme;
  publication_id: string;
  style: IPaywallStyle;
  created_at: string;
  updated_at: string;
  image_url: string;
}

// ****************************************************
// END OF PAYWALL ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST CONTENT ELEMENT SHARED TYPES
// ****************************************************

export type TiptapState = { [key: string]: any };

export enum PostThemeLineHeights {
  Less = 'Less',
  Normal = 'Normal',
  More = 'More',
}

export type PostThemeLineHeight = keyof typeof PostThemeLineHeights;

export interface PostTheme {
  quote_line_height_size?: PostThemeLineHeight;
  quote_2_line_height_size?: PostThemeLineHeight;
  quote_3_line_height_size?: PostThemeLineHeight;
  paragraph_line_height_size?: PostThemeLineHeight;
  h1_line_height_size?: PostThemeLineHeight;
  h2_line_height_size?: PostThemeLineHeight;
  h3_line_height_size?: PostThemeLineHeight;
  h4_line_height_size?: PostThemeLineHeight;
  h5_line_height_size?: PostThemeLineHeight;
  h6_line_height_size?: PostThemeLineHeight;
  list_line_height_size?: PostThemeLineHeight;
  padding?: string;
  margin_left?: string;
  margin_right?: string;
  margin_top?: string;
  margin_bottom?: string;
  border_width?: string;
  border_radius?: number;
  border_color?: string;
  background_color?: string;
  content_background_color?: string;
  max_width?: string;
  paragraph_font_size?: string;
  paragraph_font_family?: string;
  paragraph_padding_top?: string;
  paragraph_padding_bottom?: string;
  paragraph_color?: string;
  inline_link_color?: string;
  inline_link_italic?: string;
  inline_link_bold?: string;
  inline_link_underline?: string;
  inline_link_underline_color?: string;
  h1_font_size?: string;
  h1_font_family?: string;
  h1_font_color?: string;
  h1_font_weight?: string;
  h1_padding_top?: string;
  h1_padding_bottom?: string;
  h2_font_size?: string;
  h2_font_family?: string;
  h2_font_color?: string;
  h2_font_weight?: string;
  h2_padding_top?: string;
  h2_padding_bottom?: string;
  h3_font_size?: string;
  h3_font_family?: string;
  h3_font_color?: string;
  h3_font_weight?: string;
  h3_padding_top?: string;
  h3_padding_bottom?: string;
  h4_font_size?: string;
  h4_font_family?: string;
  h4_font_color?: string;
  h4_font_weight?: string;
  h4_padding_top?: string;
  h4_padding_bottom?: string;
  h5_font_size?: string;
  h5_font_family?: string;
  h5_font_color?: string;
  h5_font_weight?: string;
  h5_padding_top?: string;
  h5_padding_bottom?: string;
  h6_font_size?: string;
  h6_font_family?: string;
  h6_font_color?: string;
  h6_font_weight?: string;
  h6_padding_top?: string;
  h6_padding_bottom?: string;
  list_font_size?: string;
  list_font_family?: string;
  list_padding_left?: string;
  list_padding_right?: string;
  list_padding_top?: string;
  list_padding_bottom?: string;
  unordered_list_style_type?: string;
  ordered_list_style_type?: string;
  list_item_spacing?: string;
  list_font_color?: string;
  quote_font_size?: string;
  quote_font_family?: string;

  quote_background_color?: string;
  quote_font_color?: string;
  quote_alignment?: string;
  quote_border_color?: string;
  quote_border_width?: string;
  quote_border_radius?: string;
  quote_byline_color?: string;
  quote_byline_alignment?: string;
  quote_byline_spacing?: string;
  quote_byline_font_size?: string;
  quote_byline_font_family?: string;
  quote_padding_left?: string;
  quote_padding_right?: string;
  quote_padding_top?: string;
  quote_padding_bottom?: string;
  quote_margin_left?: string;
  quote_margin_right?: string;
  quote_margin_top?: string;
  quote_margin_bottom?: string;
  quote_2_font_size?: string;
  quote_2_font_family?: string;

  quote_2_background_color?: string;
  quote_2_font_color?: string;
  quote_2_alignment?: string;
  quote_2_border_color?: string;
  quote_2_border_left_width?: string;
  quote_2_border_radius?: string;
  quote_2_byline_color?: string;
  quote_2_byline_alignment?: string;
  quote_2_byline_spacing?: string;
  quote_2_byline_font_size?: string;
  quote_2_byline_font_family?: string;
  quote_2_padding_left?: string;
  quote_2_padding_right?: string;
  quote_2_padding_top?: string;
  quote_2_padding_bottom?: string;
  quote_2_margin_left?: string;
  quote_2_margin_right?: string;
  quote_2_margin_top?: string;
  quote_2_margin_bottom?: string;
  quote_3_font_size?: string;
  quote_3_font_family?: string;

  quote_3_background_color?: string;
  quote_3_font_color?: string;
  quote_3_alignment?: string;
  quote_3_mark_color: string;
  quote_3_mark_alignment: string;
  quote_3_mark_spacing: string;
  quote_3_mark_font_size: string;
  quote_3_mark_font_family: string;
  quote_3_byline_color?: string;
  quote_3_byline_alignment?: string;
  quote_3_byline_spacing?: string;
  quote_3_byline_font_size?: string;
  quote_3_byline_font_family?: string;
  quote_3_padding_left?: string;
  quote_3_padding_right?: string;
  quote_3_padding_top?: string;
  quote_3_padding_bottom?: string;
  quote_3_margin_left?: string;
  quote_3_margin_right?: string;
  quote_3_margin_top?: string;
  quote_3_margin_bottom?: string;
  image_padding_top?: string;
  image_padding_bottom?: string;
  image_source_y_spacing?: string;
  image_source_x_spacing?: string;
  image_source_alignment?: string;
  image_source_font_family?: string;
  image_source_style?: string;
  image_source_color?: string;
  image_source_font_size?: string;
  hyperlink_font_family?: string;
  hyperlink_background_color?: string;
  hyperlink_border_color?: string;
  hyperlink_border_width?: string;
  hyperlink_border_radius?: string;
  hyperlink_image_placement?: string;
  hyperlink_title_color?: string;
  hyperlink_title_size?: string;
  hyperlink_hide_title?: string;
  hyperlink_description_color?: string;
  hyperlink_description_size?: string;
  hyperlink_hide_description?: string;
  hyperlink_link_color?: string;
  hyperlink_link_size?: string;
  hyperlink_hide_link?: string;
  hyperlink_padding?: string;
  hyperlink_x_spacing?: string;
  hyperlink_y_spacing?: string;
  button_background_color?: string;
  button_text_color?: string;
  button_border_width?: string;
  button_border_radius?: string;
  button_border_color?: string;
  button_padding_top?: string;
  button_padding_bottom?: string;
  button_font_family?: string;
  header_top_bar_alignment?: string;
  header_top_date_format?: string;
  header_asset_id?: any;
  header_asset_integer_id?: any;
  header_asset_width?: string;
  header_asset_url?: any;
  header_title_font_family?: string;
  header_title_font_size?: string;
  header_title_alignment?: string;
  header_title_font_weight?: string;
  header_title_font_color?: string;
  header_title_vertical_padding?: string;
  header_subtitle_font_family?: string;
  header_subtitle_font_size?: string;
  header_subtitle_alignment?: string;
  header_subtitle_font_weight?: string;
  header_subtitle_font_color?: string;
  header_subtitle_vertical_padding?: string;
  header_byline_font_family?: string;
  header_byline_font_size?: string;
  header_byline_font_weight?: string;
  header_byline_font_color?: string;
  header_byline_accent_color?: string;
  header_byline_vertical_padding?: string;
  header_byline_avatar_size?: string;
  header_byline_date_format?: string;
  header_byline_underline_author?: string;
  header_byline_reading_time_enabled?: string;
  header_byline_reading_time_font_color?: string;
  header_byline_reading_time_label?: string;
  header_custom_css?: string;
  header_padding?: string;
  footer_padding?: string;
  footer_margin?: string;
  footer_background_color?: string;
  footer_font_family?: string;
  footer_font_color?: string;
  footer_top_border_width?: string;
  footer_top_border_color?: string;
  footer_top_border_size?: string;
  footer_social_background_color?: string;
  footer_social_icon_color?: string;
  content_break_y_margin?: string;
  content_break_width?: string;
  content_break_style?: string;
  content_break_color?: string;
  content_break_thickness?: string;
  table_border_width?: string;
  table_border_color?: string;
  table_padding_top?: string;
  table_padding_bottom?: string;
  table_header_font_family?: string;
  table_header_font_color?: string;
  table_header_background_color?: string;
  table_header_padding?: string;
  table_cell_font_family?: string;
  table_cell_font_color?: string;
  table_cell_background_color?: string;
  table_cell_padding?: string;
  name?: any;
  purpose?: string;
  owner_id?: string;
  header_asset?: any;
}

export interface WebTheme {
  background_color: string;
  body_font: string;
  border_color: string;
  border_radius: string;
  border_style: string;
  button_font: string;
  content_theme: string;
  header_font: string;
  logo_shadow: string;
  name: string | null;
  owner_id: string;
  primary_color: string;
  purpose: 'web';
  secondary_color: string;
  subscribe_background_color: string;
  subscribe_theme: 'light' | string;
  tertiary_color: string;
  text_on_background_color: string;
  text_on_primary_color: string;
  text_on_secondary_color: string;
  text_on_subscribe_background_color: string;
  text_on_tertiary_color: string;
}

// ****************************************************
// END OF POST CONTENT ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST COMMENTS ELEMENT SHARED TYPES
// ****************************************************

export type CommentActionType = 'delete' | 'report';

export interface CommentData {
  id: string;
  content: string;
  created_at: string;
  deleted_by_commenter_at: string;
  removed_at: string;
  subcomments_count: number;
  sub_comments: CommentData[];
  max_allowed_depth_level: number;
  commenter: string;
  depth_level: number;
  commenter_id: number | string;
  avatar_url: string;
  is_moderator_comment: boolean;
  likes: (number | string)[];
  // the value of these properties is evaluated in orchid-v2 -> PostComments component
  // as it is specific to the logged in admin or subscriber
  is_liked: boolean;
  can_delete: boolean;
}

// ****************************************************
// END OF POST COMMENTS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF SOCIALS ELEMENT SHARED TYPES
// ****************************************************

export type SocialsData = {
  twitter_url?: string;
  tiktok_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  discord_url?: string;
  threads_url?: string;
  bluesky_url?: string;
};

// ****************************************************
// END OF SOCIALS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF RECOMMENDED POSTS ELEMENT SHARED TYPES
// ****************************************************

export interface RecommendedPostData {
  id: string;
  web_title: string;
  web_subtitle: string;
  slug: string;
  image_url: string;
  audience: string;
  override_scheduled_at: string;
}

// ****************************************************
// END OF RECOMMENDED POSTS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF BREADCRUMBS ELEMENT SHARED TYPES
// ****************************************************

export interface Breadcrumb {
  href?: string;
  text: string;
}

// ****************************************************
// END OF BREADCRUMBS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST HEADER ELEMENT SHARED TYPES
// ****************************************************

export interface PostHeaderData {
  id: string;
  web_title: string;
  web_subtitle?: string;
  comments_state: string;
  override_scheduled_at: string;
  scheduled_at: string;
  /** @deprecated use is_premium instead (Never Remove, some older tip-tap content still uses this */
  audience: 'premium' | 'free' | 'both';
  is_premium: boolean;
  slug: string;
  content_tags: ContentTag[];
  image_url?: string | undefined;
  authors: Author[];
  estimated_reading_time: number | null;
  estimated_reading_time_display: string | null;
  display_thumbnail_on_web: boolean;
  author_urls: Record<string, string>;
}

export type PostHeaderCardStructure = {
  node: string;
  className: string;
  children: PostHeaderCardStructure[];
};

// ****************************************************
// END OF POST HEADER ELEMENT SHARED TYPES
// ****************************************************

export type TransitionProps = {
  transitionProperty?: CSSProperties['transition'];
  transitionDelay?: CSSProperties['transitionDelay'];
  transitionDuration?: CSSProperties['transitionDuration'];
  transitionTimingFunction?: CSSProperties['transitionTimingFunction'];
  transitionBehavior?: CSSProperties['transitionBehavior'];
  transitionFinalValue?: string | number;
};

// ****************************************************
// START OF UTM SHARED TYPES
// ****************************************************

export type UTMAttrs = {
  utmParamsEnabled?: boolean;
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
};

// ****************************************************
// END OF UTM SHARED TYPES
// ****************************************************

// ****************************************************
// START OF RSS FEED ELEMENT SHARED TYPES
// ****************************************************

export interface RSSFeedDataYouTube {
  link: RSSFeedDataYouTubeLink[];
  id: string;
  'yt:channelId': string;
  title: string;
  author: RSSFeedDataYouTubeAuthor;
  published: string;
  entry: RSSFeedDataYouTubeEntry[];
  '@xmlns:yt': string;
  '@xmlns:media': string;
  '@xmlns': string;
}

export interface RSSFeedDataYouTubeLink {
  '@rel': string;
  '@href': string;
}

export interface RSSFeedDataYouTubeAuthor {
  name: string;
  uri: string;
}

export interface RSSFeedDataYouTubeEntry {
  id: string;
  'yt:videoId': string;
  'yt:channelId': string;
  title: string;
  link: RSSFeedDataYouTubeLink;
  author: RSSFeedDataYouTubeAuthor;
  published: string;
  updated: string;
  'media:group': RSSFeedDataYouTubeMediaGroup;
}

export interface RSSFeedDataYouTubeMediaGroup {
  'media:title': string;
  'media:content': MediaContent;
  'media:thumbnail': RSSFeedDataYouTubeMediaThumbnail;
  'media:description'?: string;
  'media:community': RSSFeedDataYouTubeMediaCommunity;
}

export interface MediaContent {
  '@url': string;
  '@type': string;
  '@width': string;
  '@height': string;
}

export interface RSSFeedDataYouTubeMediaThumbnail {
  '@url': string;
  '@width': string;
  '@height': string;
}

export interface RSSFeedDataYouTubeMediaCommunity {
  'media:starRating': RSSFeedDataYouTubeMediaStarRating;
  'media:statistics': RSSFeedDataYouTubeMediaStatistics;
}

export interface RSSFeedDataYouTubeMediaStarRating {
  '@count': string;
  '@average': string;
  '@min': string;
  '@max': string;
}

export interface RSSFeedDataYouTubeMediaStatistics {
  '@views': string;
}

// ****************************************************
// END OF RSS FEED ELEMENT SHARED TYPES
// ****************************************************
